<template>
    <div>
        <v-card class="bank-statement-form" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col class="d-flex align-center" cols="12" sm="9">
                        <v-btn dark icon @click="goBack">
                            <v-icon>fal fa-arrow-left</v-icon>
                        </v-btn>
                        <div class="ml-3">{{ $t('bank_statements.heading') }}</div>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-row class="mt-3 mx-0">
                    <v-col cols="12">
                        <div class="highlight-section">
                            <div class="section-title">
                                <v-icon class="mr-3">fal fa-file-alt</v-icon>
                                {{ $t('bank_statements.section_document_info') }}
                            </div>

                            <div class="section-content">
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <v-menu
                                            v-model="picker1"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.date"
                                                    :error-messages="form.$errors.date"
                                                    :label="$t('general.date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    @blur="form.validate('date')"
                                                    @input="delete form.$errors.date"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.date"
                                                @input="picker1 = false; form.validate('date')"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col cols="12" sm="6">
                                        <v-text-field
                                            v-model="form.document_number"
                                            :label="$t('bank_statements.document_number')"
                                            dense
                                            :error-messages="form.$errors.document_number"
                                            @blur="form.validate('document_number')"
                                            @input="delete form.$errors.document_number"
                                        />
                                    </v-col>

                                    <v-col cols="12" sm="6">
                                        <v-select
                                            v-model="selectedBank"
                                            :label="$t('bank_statements.bank')"
                                            :items="banks"
                                            return-object
                                            item-text="name"
                                            dense
                                        />
                                    </v-col>

                                    <v-col class="d-flex" cols="12" sm="6">
                                        <v-select
                                            v-model="form.bank_account_id"
                                            :label="$t('bank_statements.bank_account')"
                                            :error-messages="form.$errors.bank_account_id"
                                            :items="selectedBank ? selectedBank.accounts : []"
                                            item-value="id"
                                            item-text="number"
                                            dense
                                            clearable
                                            clear-icon="fal fa-times"
                                            :disabled="!selectedBank"
                                            @blur="form.validate('bank_account_id')"
                                            @input="delete form.$errors.bank_account_id"
                                        />

                                        <v-btn
                                            class="ml-1"
                                            icon
                                            :disabled="!selectedBank"
                                            @click="openBankAccountForm"
                                        >
                                            <v-icon v-if="form.bank_account_id">fal fa-pen</v-icon>
                                            <v-icon v-else>fal fa-plus</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </div>

                            <div class="d-flex justify-end mt-2">
                                <v-btn
                                    outlined
                                    color="primary"
                                    @click="save"
                                >
                                    <v-icon left>fal fa-check</v-icon>
                                    {{ $t('bank_statements.save') }}
                                </v-btn>
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:header.actions="{ on, props }">
                        <div class="d-flex justify-end">
                            <v-btn
                                icon
                                large
                                color="primary"
                                :disabled="!bankStatement"
                                @click="openItemForm()"
                            >
                                <v-icon>fal fa-plus</v-icon>
                            </v-btn>
                        </div>
                    </template>

                    <template v-slot:item="{ item }">
                        <tr
                            :class="{
                                'font-weight-medium': true,
                                'error--text': item.getDebt().subtract(item.getCredit()).toUnit() > 0,
                                'success--text': item.getDebt().subtract(item.getCredit()).toUnit() < 0,
                            }"
                        >
                            <td>{{ item.number }}</td>
                            <td>{{ item.client.name }}</td>
                            <td>{{ $t(`client_transactions.basis.${item.basis}`) }}</td>
                            <td>{{ item.document_number }}</td>
                            <td align="right">{{ item.getDebt().toFormat('0.00') }}</td>
                            <td align="right">{{ item.getCredit().toFormat('0.00') }}</td>
                            <td align="right">{{ item.getBalance().toFormat('0.00') }}</td>
                            <td>{{ item.note }}</td>

                            <td align="center" class="w35 maxw60">
                                <v-menu
                                    bottom
                                    transition="slide-y-transition"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item :key="1" @click="openItemForm(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item :key="2" @click="deleteItem(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Item form -->
        <client-transaction-edit
            :show.sync="showItemForm"
            :item="selectedItem"
            :bank-statement-id="bankStatement ? bankStatement.id : null"
            :document-number="bankStatement ? bankStatement.document_number : null"
            :date="form.date"
            basis="payment"
            @save="getItems"
        />

        <!-- Bank Account form -->
        <bank-account-edit
            :show.sync="showBankAccountForm"
            :item="selectedBankAccount"
            :bank="selectedBank"
            @save="onBankAccountSave"
        />

    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import ClientTransaction from '@/models/ClientTransaction';
import ClientTransactionEdit from '@/views/shared/admin/clients/ClientTransactionEdit';
import BankAccountEdit from '@/views/shared/admin/bankStatements/BankAccountEdit';
import { required } from 'vuelidate/lib/validators';

export default {
    components: {
        ClientTransactionEdit,
        BankAccountEdit
    },

    async created() {
        let url = '/admin/load/bank-statements' + (this.$route.params.id ? '/' + this.$route.params.id : '');

        const data = await loadData(url);

        this.bankStatement = data.bankStatement || null;

        await this.getItems()

        this.nextDocumentNumber = data.maxDocumentNumber ? parseInt(data.maxDocumentNumber) + 1 : 1;
        this.banks = data.banks;
        this.selectedBank = this.banks.find(b => b.accounts.some(a => a.id === this.bankStatement?.bank_account_id))
        this.form = this.createForm()
    },

    data() {
        return {
            bankStatement: null,
            nextDocumentNumber: null,
            banks: [],
            form: this.createForm(),
            showItemForm: false,
            headers: [
                { text: this.$t('general.number'), value: 'number' },
                { text: this.$tc('general.client', 1), value: 'client.name' },
                { text: this.$t('client_transactions.basis.heading'), value: 'basis' },
                { text: this.$t('client_transactions.document_number'), value: 'document_number' },
                { text: this.$t('client_transactions.debt'), value: 'debt', align: 'right' },
                { text: this.$t('client_transactions.credit'), value: 'credit', align: 'right' },
                { text: this.$t('client_transactions.balance'), value: 'balance', align: 'right' },
                { text: this.$t('general.note'), value: 'note', sortable: false },
                { text: '', value: 'actions', sortable: false }
            ],

            pagination: {
                sortBy: ['number'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },

            selectedItem: null,
            picker1: false,
            bankStatementNumberPrefix: moment().format('YY/'),

            selectedBank: null,
            selectedBankAccount: null,
            showBankAccountForm: false,

            items: [],
            itemsTotal: 0,
            loading: false,
        };
    },

    validations: {
        form: {
            date: { required },
            document_number: { required },
            bank_account_id: { required },
        }
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        selectedBank(val) {
            if(val?.accounts.some(a => a.id === this.form.bank_account_id)) return
            this.form.bank_account_id = null
        }
    },

    methods: {
        getItems() {
            if(!this.bankStatement) return

            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/client-transactions?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}&bankStatementId=${this.bankStatement.id}`;

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.items = response.data.data.map(item => new ClientTransaction(item));
                this.itemsTotal = response.data.total;
            });
        },

        createForm() {
            return new Form(this, 'form', {
                id: this.bankStatement?.id || null,
                user_id: this.user?.id || null,
                bank_account_id: this.bankStatement?.bank_account_id || null,
                date: this.bankStatement?.date
                    ? moment(this.bankStatement.date).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD'),
                document_number: this.bankStatement?.document_number || this.bankStatementNumberPrefix + this.nextDocumentNumber,
            });
        },

        async openItemForm(item = null) {
            if(!this.form.validate()) return;

            this.selectedItem = item;
            this.showItemForm = true;
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('client_transactions.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                async result => {
                    if (!result) return;

                    await ApiService.delete(`/admin/client-transactions/${item.id}`, {})

                    this.$snackbar(this.$t('client_transactions.deleted'), 'success');

                    this.getItems()
                }
            );
        },

        async save() {
            if(!this.form.validate()) return;

            if (!this.form.id) {
                const response = await ApiService.post('/admin/bank-statements', this.form);
                this.bankStatement = response.data;
                this.form.id = response.data.id;
                this.$snackbar(this.$t('bank_statements.saved'), 'success');

                this.$router.replace({
                    name: 'adminBankStatementUpdate',
                    params: { id: response.data.id }
                });
            } else {
                const response = await ApiService.patch(`/admin/bank-statements/${this.form.id}`, this.form);
                this.bankStatement = response.data;
                this.$snackbar(this.$t('bank_statements.updated'), 'success');
            }
        },

        openBankAccountForm() {
            if(!this.selectedBank) return

            this.selectedBankAccount = this.selectedBank?.accounts?.find(account => account.id === this.form.bank_account_id) || null
            this.showBankAccountForm = true
        },

        async onBankAccountSave(bankAccount) {
            this.form.bank_account_id = bankAccount.id

            if(!this.selectedBankAccount) {
                this.selectedBank.accounts.push(bankAccount)
            } else {
                this.selectedBank.accounts.forEach((account, index, array) => {
                    if(account.id === bankAccount.id) {
                        this.$set(array, index, bankAccount)
                    }
                })
            }

            this.form.validate('bank_account_id')
            this.showBankAccountForm = false
        },

        goBack() {
            this.$router.push({ name: 'adminBankStatements' }).catch(err => {});
        },
    }
};
</script>

<style lang="scss" scoped>
.bank-statement-form {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .highlight-section {
        border-radius: 3px;
        background: #fafafa;
        border: 1px solid #cccccc;
        padding: 20px;
        font-size: 15px;

        .section-title {
            margin-bottom: 24px;
            font-size: 20px;
            color: black;

            .v-icon {
                color: black;
            }
        }
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }
    }
}

.bank-account-form-header {
    text-overflow: ellipsis;
    overflow-x: clip;
}
</style>
